import React from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import DynamicModule from './DynamicModule';

/**
 * DynamicModulePage - Route component for config-driven modules
 *
 * URL structure:
 * - /buildings           → First record, panel collapsed
 * - /buildings/123       → Record 123, panel collapsed
 * - /buildings/123?panel=open  → Record 123, panel expanded
 * - /buildings?search=FAB      → Search for FAB
 *
 * DynamicModule uses the dynamic API which auto-resolves FK display values.
 */

// Map URL slugs to table names
const slugToTableName = {
  'buildings': 'Building',
  'drawings': 'Drawing',
  'users': 'Admin_Users',
  'source': 'Source',
  'discipline': 'Discipline',
  'commissioning-key': 'Commissioning_Key',
  'drawing-log': 'Drawing_Log',
  'ip-address-sln': 'IP_Address_SLN',
  'ip-address-dln': 'IP_Address_DLN',
  'organization': 'Organization',
  'card-type': 'Card_Type',
  'networks-enet': 'Networks_ENET',
  'networks-serial': 'Networks_Serial',
  'plc-msg-map': 'PLC_MSG_Map',
  'tags': 'Tags',
  'eng-units': 'Eng_Units',
  'isa-type': 'ISA_Type',
  'index-sheet': 'Index_Sheet',
  'manufacturer': 'Manufacturer',
  'panel-terminal': 'Panel_Terminal',
  'panel-type': 'Panel_Type',
  'panels': 'Panels',
  'pwr-dist': 'Pwr_Dist',
  'soo-index': 'SOO_Index',
  'soo-log': 'SOO_Log',
  'soo-equipment': 'SOO_Equipment',
  'subnet': 'Subnet',
  'rfi-log': 'MGMT_TX_RFILog',
  'system-code': 'System_Code',
  'transfer-party': 'Transfer_Party',
  'valve-size': 'Valve_Size',
  'wire-type': 'Wire_Type'
};

// Map table names to URL slugs (for navigation)
export const tableNameToSlug = {
  'Building': 'buildings',
  'Drawing': 'drawings',
  'Admin_Users': 'users',
  'Source': 'source',
  'Discipline': 'discipline',
  'Commissioning_Key': 'commissioning-key',
  'Drawing_Log': 'drawing-log',
  'IP_Address_SLN': 'ip-address-sln',
  'IP_Address_DLN': 'ip-address-dln',
  'Organization': 'organization',
  'Card_Type': 'card-type',
  'Networks_ENET': 'networks-enet',
  'Networks_Serial': 'networks-serial',
  'PLC_MSG_Map': 'plc-msg-map',
  'Tags': 'tags',
  'Eng_Units': 'eng-units',
  'ISA_Type': 'isa-type',
  'Index_Sheet': 'index-sheet',
  'Manufacturer': 'manufacturer',
  'Panel_Terminal': 'panel-terminal',
  'Panel_Type': 'panel-type',
  'Panels': 'panels',
  'Pwr_Dist': 'pwr-dist',
  'SOO_Index': 'soo-index',
  'SOO_Log': 'soo-log',
  'SOO_Equipment': 'soo-equipment',
  'Subnet': 'subnet',
  'MGMT_TX_RFILog': 'rfi-log',
  'System_Code': 'system-code',
  'Transfer_Party': 'transfer-party',
  'Valve_Size': 'valve-size',
  'Wire_Type': 'wire-type'
};

const DynamicModulePage = () => {
  const location = useLocation();
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();

  // Extract slug from URL path — skip dbSlug prefix (e.g., "/gwa/buildings/123" -> "buildings")
  const pathParts = location.pathname.slice(1).split('/');
  const urlSlug = pathParts.length > 1 ? pathParts[1] : pathParts[0];

  // Convert URL slug to table name
  const tableName = slugToTableName[urlSlug] || urlSlug;

  // Extract URL state
  const urlRecordId = recordId ? parseInt(recordId, 10) : null;
  const urlSearch = searchParams.get('search') || '';
  const urlPanelOpen = searchParams.get('panel') === 'open';
  const urlRelatedCollapsed = searchParams.get('related') === 'collapsed';

  return (
    <DynamicModule
      tableName={tableName}
      urlRecordId={urlRecordId}
      urlSearch={urlSearch}
      urlPanelOpen={urlPanelOpen}
      urlRelatedCollapsed={urlRelatedCollapsed}
    />
  );
};

export default DynamicModulePage;
